import { z } from "zod";

import { EMPLOYMENT_TYPES, JOB_STATUSES, SENIORITY_LEVELS, WORK_MODELS } from "../types/job";

const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;

/** One scoring criterion (Option D). Importance 1-10, required is the
 * knock-out flag, description is optional context. */
export const jobCriterionSchema = z.object({
  name: z.string().min(1).max(200),
  importance: z.number().int().min(1).max(10),
  required: z.boolean(),
  description: z.string().max(500),
});

/** Base object for Screen 2 (Stelle anlegen). Kept as a plain `ZodObject`
 * (no refinements) so `jobSchema` below can still `.extend()` it — a
 * `.refine()` returns a `ZodEffects` that has no `.extend` / `.shape`. */
const jobCreateObject = z.object({
  title: z.string().min(1, "Jobtitel ist erforderlich"),
  company: z.string().min(1, "Unternehmen ist erforderlich"),
  location: z.string().min(1, "Standort ist erforderlich"),
  workModel: z.enum(WORK_MODELS).nullable(),
  /** Coordinates of a place picked for the Standort (#49); null when the
   * text wasn't picked, so the search geocodes it. */
  lat: z.number().min(-MAX_LATITUDE).max(MAX_LATITUDE).nullable(),
  lng: z.number().min(-MAX_LONGITUDE).max(MAX_LONGITUDE).nullable(),
  /** Branche — optional, max 100 chars (mirrors backend CharField).
   * Empty string is the "not set" value (matches Django CharField default). */
  industry: z.string().max(100),
  /** 1–3 sentence summary. Empty string allowed because the structured
   * blocks below now carry the substance — Zod `min(1)` removed. */
  description: z.string(),
  tasks: z.array(z.string().max(300)),
  benefits: z.array(z.string().max(300)),
  hiringPriorities: z.array(z.string().max(250)),
  criteria: z.array(jobCriterionSchema),
  seniority: z.enum(SENIORITY_LEVELS).nullable(),
  employmentType: z.enum(EMPLOYMENT_TYPES).nullable(),
  salaryFrom: z.number().int().nonnegative().nullable(),
  salaryTo: z.number().int().nonnegative().nullable(),
  /** ISO `YYYY-MM-DD` or null. Loose check — the date picker emits this. */
  targetStartDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Datum im Format JJJJ-MM-TT")
    .nullable(),
  notes: z.string().nullable(),
  sourceUrl: z.string().url().nullable(),
});

/** A given salary floor must not exceed the ceiling. Nulls are
 * open-ended, so a missing bound never trips the check. Shared by the
 * create schema and the full `jobSchema` so both reject an inverted range. */
const salaryRangeOrdered = (v: { salaryFrom: number | null; salaryTo: number | null }): boolean =>
  v.salaryFrom == null || v.salaryTo == null || v.salaryFrom <= v.salaryTo;

const salaryRangeParams = {
  path: ["salaryTo"],
  message: "Das maximale Gehalt darf nicht kleiner als das minimale Gehalt sein.",
};

/** Narrow input shape for Screen 2 (Stelle anlegen). The Zod schema is the
 * single source of truth for the form + backend validation; the TS type is
 * inferred from it. */
export const jobCreateSchema = jobCreateObject.refine(salaryRangeOrdered, salaryRangeParams);

export type JobCreateInput = z.infer<typeof jobCreateSchema>;

export const jobSchema = jobCreateObject
  // Coordinates are only sent with a picked place; a job reports `distanceAvailable`.
  .omit({ lat: true, lng: true })
  .extend({
    id: z.string(),
    createdBy: z.string(),
    status: z.enum(JOB_STATUSES),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .refine(salaryRangeOrdered, salaryRangeParams);
